// Variant + pricing + stock helpers. A product either sells as a single SKU or
// through variants, never both - every helper takes an optional variant so one
// function serves both shapes.

import type { Product, ProductVariant } from './types'

export const hasVariants = (p: Product): boolean => p.variants.length > 0

/**
 * Available stock for a product or one of its variants, read from the
 * `stockBySku` map derived from `/inventory`. With no variant given on a
 * product that sells in variants, sums every variant's available stock (the
 * product-level answer a shopper sees before picking one).
 */
export function availableFor(
  product: Product,
  stockBySku: Record<string, number>,
  variant?: ProductVariant | null,
): number {
  if (variant) return stockBySku[variant.sku] ?? 0
  if (hasVariants(product)) {
    return product.variants.reduce((sum, v) => sum + (stockBySku[v.sku] ?? 0), 0)
  }
  return stockBySku[product.sku] ?? 0
}

export const priceOf = (product: Product, variant?: ProductVariant | null): number =>
  variant ? variant.price : product.price

export const compareAtOf = (product: Product, variant?: ProductVariant | null): number | null =>
  variant ? variant.compareAtPrice : product.compareAtPrice

export const isOnSale = (product: Product, variant?: ProductVariant | null): boolean =>
  variant ? variant.onSale : product.onSale

export function discountPct(product: Product, variant?: ProductVariant | null): number {
  const was = compareAtOf(product, variant)
  const now = priceOf(product, variant)
  return was && was > now ? Math.round(((was - now) / was) * 100) : 0
}

/** Best (highest) discount across a product's variants, or its own if it has none. */
export const bestDiscount = (product: Product): number =>
  hasVariants(product)
    ? Math.max(0, ...product.variants.map((v) => discountPct(product, v)))
    : discountPct(product)

export const isSoldOut = (
  product: Product,
  stockBySku: Record<string, number>,
  variant?: ProductVariant | null,
): boolean => availableFor(product, stockBySku, variant) <= 0

export function isLowStock(
  product: Product,
  stockBySku: Record<string, number>,
  variant?: ProductVariant | null,
): boolean {
  const a = availableFor(product, stockBySku, variant)
  return a > 0 && a <= 5
}

/** First non-sold-out variant, falling back to the first variant if all are out. */
export function firstAvailableVariant(
  product: Product,
  stockBySku: Record<string, number>,
): ProductVariant | null {
  return product.variants.find((v) => !isSoldOut(product, stockBySku, v)) ?? product.variants[0] ?? null
}

/** Display label for a variant, e.g. "50ml / Black". */
export const variantLabel = (variant: ProductVariant): string =>
  Object.values(variant.options).join(' / ')
